/**
 * logger - Production-safe logging wrapper
 *
 * Security: Prevents sensitive data (tokens, emails, raw API response bodies)
 * from leaking into the browser console in production environments.
 *
 * Behaviour:
 *  - Development  → full output forwarded to console (no redaction)
 *  - Production   → sensitive fields are redacted before logging
 *
 * Sensitive fields redacted in production: token, email, data, password,
 * authorization, cookie, set-cookie, x-user-email.
 *
 * Feature: Epic 1.2 – Security Hardening / Feature 1.2.3
 *
 * @example
 * import { logger } from '@lib/logger';
 * logger.error('[MyService] Something went wrong:', error);
 */

/** Fields that must never appear in production console output. */
const SENSITIVE_KEYS = new Set([
  'token',
  'email',
  'data',
  'password',
  'authorization',
  'cookie',
  'set-cookie',
  'x-user-email',
]);

const REDACTED = '[REDACTED]';

/**
 * Recursively redacts sensitive keys from a plain object.
 * Non-object values are returned as-is.
 */
function redact(value: unknown, depth = 0): unknown {
  // Guard against deep/circular structures
  if (depth > 5) return value;

  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1));

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      result[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? REDACTED : redact(v, depth + 1);
    }
    return result;
  }

  return value;
}

/**
 * Sanitizes an array of log arguments for production.
 * Strings are passed through; objects are deep-redacted.
 * Errors have their message kept but stack and cause stripped.
 */
function sanitizeArgs(args: unknown[]): unknown[] {
  return args.map((arg) => {
    if (arg instanceof Error) {
      // Keep the safe top-level message, drop potentially sensitive stack/cause
      return { name: arg.name, message: arg.message };
    }
    if (typeof arg === 'object' && arg !== null) {
      return redact(arg);
    }
    return arg;
  });
}

const IS_PROD = import.meta.env.PROD;

/**
 * Production-safe logger.
 *
 * In development  → delegates directly to console, no overhead.
 * In production   → redacts sensitive fields before forwarding to console.
 */
export const logger = {
  /** Log an error. Sensitive fields are redacted in production. */
  error(...args: unknown[]): void {
    if (IS_PROD) {
      console.error(...sanitizeArgs(args));
    } else {
      console.error(...args);
    }
  },

  /** Log a warning. Sensitive fields are redacted in production. */
  warn(...args: unknown[]): void {
    if (IS_PROD) {
      console.warn(...sanitizeArgs(args));
    } else {
      console.warn(...args);
    }
  },

  /** Log an info message. No redaction needed (should not carry sensitive data). */
  info(...args: unknown[]): void {
    if (!IS_PROD) {
      console.info(...args);
    }
    // info is suppressed entirely in production
  },

  /** Log a debug message. Suppressed in production. */
  debug(...args: unknown[]): void {
    if (!IS_PROD) {
      console.debug(...args);
    }
  },
} as const;
